// ═══════════════════════════════════════════════════════════════════════════════
// API Client
// ═══════════════════════════════════════════════════════════════════════════════

import { CLIENT_CHANNEL_HEADER, getClientChannel } from './clientChannel';
import { emitLimitReachedFromHttp } from './limitError';

const API_BASE = '/api';
const NON_REFRESHABLE_401_CODES = new Set([
  'PASSWORD_INCORRECT',
  'CURRENT_PASSWORD_INCORRECT',
]);

type ApiResponse<T> = {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  code?: string;
};

type RequestOptions = {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
};

/** Thrown on non-OK API responses; carries optional `code` and fields from the JSON body for UI mapping. */
export class ApiClientError extends Error {
  readonly status: number;

  readonly code: string | undefined;

  readonly body: Record<string, unknown>;

  constructor(message: string, status: number, body: Record<string, unknown>) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.body = body;
    this.code = typeof body.code === 'string' ? body.code : undefined;
  }
}

const withInFlightDedupe = <TArgs extends unknown[], TResult>(
  keyFn: (...args: TArgs) => string,
  factory: (...args: TArgs) => Promise<TResult>,
): ((...args: TArgs) => Promise<TResult>) => {
  const inFlight = new Map<string, Promise<TResult>>();
  return (...args: TArgs): Promise<TResult> => {
    const key = keyFn(...args);
    const pending = inFlight.get(key);
    if (pending) {
      return pending;
    }
    const request = factory(...args).finally(() => {
      inFlight.delete(key);
    });
    inFlight.set(key, request);
    return request;
  };
};

const getStoredToken = (): string | null =>
  localStorage.getItem('accessToken');

const setStoredToken = (token: string): void =>
  localStorage.setItem('accessToken', token);

const removeStoredToken = (): void =>
  localStorage.removeItem('accessToken');

const refreshState: { inFlight: Promise<boolean> | null } = {
  inFlight: null,
};

const buildHeaders = (options?: RequestOptions): HeadersInit => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    [CLIENT_CHANNEL_HEADER]: getClientChannel(),
    ...options?.headers,
  };

  const token = getStoredToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

const handleResponse = async <T>(response: Response): Promise<ApiResponse<T>> => {
  const json = (await response.json()) as Record<string, unknown> & {
    success?: boolean;
    error?: string;
    code?: string;
  };

  if (!response.ok) {
    if (response.status === 503 && json.code === 'MAINTENANCE') {
      window.dispatchEvent(new CustomEvent('fdh:maintenance'));
    }
    // Re-consent interceptor: redirect to consent page on version mismatch
    if (response.status === 403 && json.code === 'CONSENT_REQUIRED') {
      window.location.href = '/consent';
      throw new Error('CONSENT_REQUIRED');
    }

    // Try to refresh token on 401
    const shouldRefreshToken =
      response.status === 401 &&
      getStoredToken() &&
      !NON_REFRESHABLE_401_CODES.has(typeof json.code === 'string' ? json.code : '');
    if (shouldRefreshToken) {
      const refreshed = await tryRefreshToken();
      if (refreshed) {
        throw new Error('TOKEN_REFRESHED');
      }
      removeStoredToken();
      localStorage.removeItem('user');
      window.location.href = '/';
    }

    const msg =
      typeof json.error === 'string' && json.error.trim() !== ''
        ? json.error
        : `Request failed with status ${response.status}`;
    emitLimitReachedFromHttp(response.status, json as Record<string, unknown>);
    throw new ApiClientError(msg, response.status, json as Record<string, unknown>);
  }

  return json as ApiResponse<T>;
};

const tryRefreshToken = async (): Promise<boolean> => {
  if (refreshState.inFlight) {
    return refreshState.inFlight;
  }

  refreshState.inFlight = (async () => {
    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          [CLIENT_CHANNEL_HEADER]: getClientChannel(),
        },
      });

      if (!response.ok) {
        return false;
      }

      const json = (await response.json()) as {
        success?: boolean;
        data?: { accessToken?: string };
      };
      if (json.success && json.data?.accessToken) {
        setStoredToken(json.data.accessToken);
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      refreshState.inFlight = null;
    }
  })();

  return refreshState.inFlight;
};

export const apiRequest = async <T>(
  path: string,
  options?: RequestOptions
): Promise<ApiResponse<T>> => {
  const url = `${API_BASE}${path}`;
  const fetchOptions: RequestInit = {
    method: options?.method ?? 'GET',
    headers: buildHeaders(options),
    credentials: 'include',
  };

  if (options?.body) {
    fetchOptions.body = JSON.stringify(options.body);
  }

  try {
    const response = await fetch(url, fetchOptions);
    return await handleResponse<T>(response);
  } catch (error) {
    // Retry once after token refresh
    if (error instanceof Error && error.message === 'TOKEN_REFRESHED') {
      const response = await fetch(url, {
        ...fetchOptions,
        headers: buildHeaders(options),
      });
      return await handleResponse<T>(response);
    }
    throw error;
  }
};

// Convenience methods
export const api = {
  get: <T>(path: string) => apiRequest<T>(path),

  post: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, { method: 'POST', body }),

  put: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, { method: 'PUT', body }),

  delete: <T>(path: string) =>
    apiRequest<T>(path, { method: 'DELETE' }),
};

// ═══════════════════════════════════════════════════════════════════════════════
// Task API
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  Task,
  TaskListItem,
  TodayTodoItem,
  EmployerPendingReviewItem,
  EmployerApprovedItem,
  EmployerTodayTaskItem,
  EmployerOverdueTaskItem,
  TaskInstanceWithSubmission,
  CreateTaskInput,
  UpdateTaskInput,
  SubmitTaskInput,
  ReviewTaskInput,
  ApprovalStatus,
  CommentListResponse,
  CommentWithUser,
  CommentTargetType,
  CreateCommentInput,
  PaginatedResponse,
  Language,
  NotificationListResponse,
  FileUploadResponse,
  NotificationUnreadCountResponse,
  BudgetListItem,
  Budget,
  BudgetSummary,
  CreateBudgetInput,
  UpdateBudgetInput,
  CreateReceiptInput,
  UpdateReceiptInput,
  ReviewReceiptInput,
  Receipt,
  ReceiptListItem,
  SettlementListItem,
  SettlementSummary,
  SettlementByBudgetResponse,
  SettlementDetailResponse,
  CreateSettlementInput,
  FamilyMember,
  FamilyMemberListItem,
  CreateFamilyMemberInput,
  UserProfile,
  UserSettings,
  UpdateSettingsInput,
  UsageRegion,
  FaqAudienceRole,
  FaqLocalizedItem,
  LegalDocumentLocale,
  LegalDocumentPublic,
  LegalDocumentType,
  PinMessage,
  PinMessageListResponse,
  CreatePinMessageInput,
  UpdatePinMessageInput,
  ReorderPinMessageInput,
  PinMessageLimits,
  SystemMessageListResponse,
  TaskGroupDetail,
  TaskGroupListItem,
  PickupDropoffGroupCreateInput,
  MedicationReminderGroupCreateInput,
  WeatherGreetingCurrentResponse,
} from '@fdh/shared';
import type {
  InventorySubmitInputSchema,
  PairingImportBodySchema,
  PairingImportModalState,
  PairingImportPreview,
  PairingImportResult,
  RestorableArchivedTaskItem,
  SubscriptionInvoiceListResult,
  RestoreArchivedTasksModalState,
  RestoreArchivedTasksResult,
  SubscriptionCheckoutResult,
  SubscriptionPortalResult,
  SubscriptionPreview,
  SubscriptionStatusSummary,
  UserUsageSummary,
  MembershipPricingLocale,
  PublicMembershipPricingResponse,
} from '@fdh/shared';
import { uploadReceiptPhotoViaBackend, uploadTaskPhotoViaBackend } from './directPhotoUpload';

type ReviewSettlementInput = {
  status: 'rejected' | 'reviewed';
  rejectedReason?: string;
};

type ReceiptStatus = 'save' | 'submitted' | 'reviewed';

type TaskListFilter = 'all' | 'active' | 'archived';

type TodayTodoResponse = {
  tasks: TodayTodoItem[];
  pendingReviewCount: number;
};

type HelperTodoListResponse = {
  tasks: TodayTodoItem[];
  total: number;
};

type PendingReviewListResponse = {
  tasks: EmployerPendingReviewItem[];
  total: number;
};

type ApprovedListResponse = {
  tasks: EmployerApprovedItem[];
  total: number;
};

type EmployerTodayListResponse = {
  tasks: EmployerTodayTaskItem[];
  total: number;
};

type EmployerOverdueListResponse = {
  tasks: EmployerOverdueTaskItem[];
  total: number;
};

export const taskApi = {
  // List tasks for current pairing (matches backend PaginatedResponse)
  list: (filter: TaskListFilter = 'all', page = 1, pageSize = 20) =>
    api.get<PaginatedResponse<TaskListItem>>(
      `/tasks?filter=${filter}&page=${page}&pageSize=${pageSize}`,
    ),

  // Get task detail
  get: (id: string) => api.get<Task>(`/tasks/${id}`),

  // Create task (employer only)
  create: (data: CreateTaskInput) => api.post<Task>('/tasks', data),

  // Update task (employer only)
  update: (id: string, data: UpdateTaskInput) => api.put<Task>(`/tasks/${id}`, data),

  // Archive task (employer only)
  archive: (id: string) => api.delete<{ success: boolean }>(`/tasks/${id}`),
};

export const taskGroupApi = {
  list: () => api.get<{ groups: TaskGroupListItem[] }>('/task-groups'),
  get: (groupId: string) => api.get<TaskGroupDetail>(`/task-groups/${encodeURIComponent(groupId)}`),
  resolveFromTask: (taskId: string) =>
    api.get<{ groupId: string }>(`/task-groups/resolve-from-task/${encodeURIComponent(taskId)}`),
  createPickupDropoff: (body: PickupDropoffGroupCreateInput) =>
    api.post<TaskGroupDetail>('/task-groups/pickup-dropoff', body),
  updatePickupDropoff: (groupId: string, body: PickupDropoffGroupCreateInput) =>
    api.put<TaskGroupDetail>(`/task-groups/${encodeURIComponent(groupId)}/pickup-dropoff`, body),
  createMedicationReminder: (body: MedicationReminderGroupCreateInput) =>
    api.post<TaskGroupDetail>('/task-groups/medication-reminder', body),
  updateMedicationReminder: (groupId: string, body: MedicationReminderGroupCreateInput) =>
    api.put<TaskGroupDetail>(`/task-groups/${encodeURIComponent(groupId)}/medication-reminder`, body),
};

export const taskInstanceApi = {
  // Employer: list instances submitted and awaiting review
  pendingReview: () => api.get<PendingReviewListResponse>('/task-instances/pending'),

  // Employer: list approved instances
  approvedList: () => api.get<ApprovedListResponse>('/task-instances/approved'),

  // Employer: today's helper tasks with statuses
  employerTodayList: () => api.get<EmployerTodayListResponse>('/task-instances/employer-today'),

  // Employer: all instances for a single due date
  employerByDate: (date: string) =>
    api.get<EmployerTodayListResponse>(
      `/task-instances/employer-by-date?date=${encodeURIComponent(date)}`,
    ),

  // Employer: overdue unsubmitted tasks
  overdueList: () => api.get<EmployerOverdueListResponse>('/task-instances/overdue'),

  // Employer: upcoming instances (tomorrow … today+7 in user timezone)
  employerFuture: (startDate: string, endDate: string) =>
    api.get<HelperTodoListResponse>(
      `/task-instances/employer-future?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
    ),

  // Get today's todo list (helper view)
  today: (viewerLanguage?: Language) => {
    const sp = new URLSearchParams();
    if (viewerLanguage) {
      sp.set('viewerLanguage', viewerLanguage);
    }
    const query = sp.toString();
    const suffix = query ? `?${query}` : '';
    return api.get<TodayTodoResponse>(`/task-instances/today${suffix}`);
  },
  tomorrow: (viewerLanguage?: Language) => {
    const sp = new URLSearchParams();
    if (viewerLanguage) {
      sp.set('viewerLanguage', viewerLanguage);
    }
    const query = sp.toString();
    const suffix = query ? `?${query}` : '';
    return api.get<HelperTodoListResponse>(`/task-instances/tomorrow${suffix}`);
  },
  tomorrowCount: () => api.get<{ total: number }>('/task-instances/tomorrow/count'),

  helperFuture: (startDate: string, endDate: string, viewerLanguage?: Language) => {
    const sp = new URLSearchParams({
      startDate,
      endDate,
    });
    if (viewerLanguage) {
      sp.set('viewerLanguage', viewerLanguage);
    }
    return api.get<HelperTodoListResponse>(`/task-instances/helper-future?${sp.toString()}`);
  },
  helperOverdue: (viewerLanguage?: Language) => {
    const sp = new URLSearchParams();
    if (viewerLanguage) {
      sp.set('viewerLanguage', viewerLanguage);
    }
    const query = sp.toString();
    const suffix = query ? `?${query}` : '';
    return api.get<HelperTodoListResponse>(`/task-instances/helper-overdue${suffix}`);
  },
  helperOverdueCount: () =>
    api.get<{ total: number }>('/task-instances/helper-overdue/count'),
  history: (
    startDate: string,
    endDate: string,
    status: ApprovalStatus | 'all' | 'not_submitted',
    viewerLanguage?: Language,
  ) => {
    const sp = new URLSearchParams({
      startDate,
      endDate,
      status,
    });
    if (viewerLanguage) {
      sp.set('viewerLanguage', viewerLanguage);
    }
    return api.get<HelperTodoListResponse>(`/task-instances/history?${sp.toString()}`);
  },

  // Helper: multipart upload via backend (preferred — no GCS signed URL)
  uploadPhoto: (blob: Blob, fileName: string) =>
    uploadTaskPhotoViaBackend(blob, fileName).then((url) => ({ url })),

  // Legacy signed PUT URL (may fail on Cloud Run without GCS_KEY_FILE)
  requestUploadUrl: (contentType: string) =>
    api.post<FileUploadResponse>('/task-instances/upload-url', { contentType }),

  // Get task instance detail with submission
  get: withInFlightDedupe(
    (id: string, viewerLanguage?: Language) => `${id}:${viewerLanguage ?? ''}`,
    (id: string, viewerLanguage?: Language) => {
      const sp = new URLSearchParams();
      if (viewerLanguage) {
        sp.set('viewerLanguage', viewerLanguage);
      }
      const query = sp.toString();
      const suffix = query ? `?${query}` : '';
      return api.get<TaskInstanceWithSubmission>(
        `/task-instances/${encodeURIComponent(id)}${suffix}`,
      );
    },
  ),

  // Submit task completion (helper only)
  submit: (id: string, data: SubmitTaskInput) =>
    api.post<TaskInstanceWithSubmission>(`/task-instances/${id}/submit`, data),

  // Review task submission (employer only)
  review: (id: string, data: ReviewTaskInput) =>
    api.put<TaskInstanceWithSubmission>(`/task-instances/${id}/review`, data),

  // Resubmit after needs_update (helper only)
  resubmit: (id: string, data: SubmitTaskInput) =>
    api.put<TaskInstanceWithSubmission>(`/task-instances/${id}/resubmit`, data),

  // Cancel pending submission
  cancel: (id: string) =>
    api.put<TaskInstanceWithSubmission>(`/task-instances/${id}/cancel`, {}),
};

export const geocodingApi = {
  reverse: (lat: number, lon: number, locale?: Language) => {
    const params = new URLSearchParams({ lat: String(lat), lon: String(lon) });
    if (locale) {
      params.set('locale', locale);
    }
    return api.get<{ displayName: string }>(`/geocoding/reverse?${params.toString()}`);
  },
};

export const budgetApi = {
  list: (
    status: 'active' | 'settled' | 'all' = 'all',
    page = 1,
    pageSize = 50,
    startDate?: string,
    endDate?: string
  ) => {
    const params = new URLSearchParams({
      status,
      page: String(page),
      pageSize: String(pageSize),
    });
    if (startDate) {
      params.set('startDate', startDate);
    }
    if (endDate) {
      params.set('endDate', endDate);
    }
    return api.get<PaginatedResponse<BudgetListItem>>(`/budgets?${params.toString()}`);
  },

  get: (id: string) => api.get<BudgetSummary>(`/budgets/${id}`),

  create: (body: CreateBudgetInput) => api.post<Budget>('/budgets', body),

  update: (id: string, body: UpdateBudgetInput) => api.put<Budget>(`/budgets/${id}`, body),

  remove: (id: string) => api.delete<{ success: boolean }>(`/budgets/${id}`),
};

export const settlementApi = {
  preview: (params: CreateSettlementInput) => {
    const q = new URLSearchParams({
      mainBudgetId: params.mainBudgetId,
    });
    return api.get<SettlementSummary>(`/settlements/preview?${q.toString()}`);
  },

  create: (body: CreateSettlementInput) =>
    api.post<SettlementSummary>('/settlements', body),

  list: (locked: 'all' | 'true' | 'false' = 'all', page = 1, pageSize = 50) =>
    api.get<PaginatedResponse<SettlementListItem>>(
      `/settlements?locked=${locked}&page=${page}&pageSize=${pageSize}`,
    ),

  get: (id: string, viewerLanguage?: string) => {
    const q = viewerLanguage?.trim()
      ? `?viewerLanguage=${encodeURIComponent(viewerLanguage.trim())}`
      : '';
    return api.get<SettlementDetailResponse>(`/settlements/${id}${q}`);
  },

  getByBudget: (budgetId: string, viewerLanguage?: string) => {
    const q = viewerLanguage?.trim()
      ? `?viewerLanguage=${encodeURIComponent(viewerLanguage.trim())}`
      : '';
    return api.get<SettlementByBudgetResponse>(`/settlements/by-budget/${budgetId}${q}`);
  },

  review: (id: string, body: ReviewSettlementInput) =>
    api.put<SettlementDetailResponse>(`/settlements/${id}/review`, body),

  withdraw: (id: string) => api.put<SettlementDetailResponse>(`/settlements/${id}/withdraw`),

  unlock: (id: string) => api.put<SettlementDetailResponse>(`/settlements/${id}/unlock`),
};

type ReceiptListQuery = {
  budgetId?: string;
  status?: 'all' | ReceiptStatus;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
};

export const receiptApi = {
  uploadPhoto: (blob: Blob, fileName: string) =>
    uploadReceiptPhotoViaBackend(blob, fileName).then((url) => ({ url })),

  requestUploadUrl: (contentType: string) =>
    api.post<FileUploadResponse>('/receipts/upload-url', { contentType }),

  create: (data: CreateReceiptInput) => api.post<Receipt>('/receipts', data),

  list: (params: ReceiptListQuery = {}) => {
    const sp = new URLSearchParams();
    sp.set('status', params.status ?? 'all');
    sp.set('page', String(params.page ?? 1));
    sp.set('pageSize', String(params.pageSize ?? 20));
    if (params.budgetId) {
      sp.set('budgetId', params.budgetId);
    }
    if (params.startDate) {
      sp.set('startDate', params.startDate);
    }
    if (params.endDate) {
      sp.set('endDate', params.endDate);
    }
    return api.get<PaginatedResponse<ReceiptListItem>>(`/receipts?${sp.toString()}`);
  },

  get: (id: string, viewerLanguage?: Language) => {
    const sp = new URLSearchParams();
    if (viewerLanguage) {
      sp.set('viewerLanguage', viewerLanguage);
    }
    const query = sp.toString();
    const suffix = query ? `?${query}` : '';
    return api.get<Receipt>(`/receipts/${encodeURIComponent(id)}${suffix}`);
  },

  update: (id: string, data: UpdateReceiptInput) =>
    api.put<Receipt>(`/receipts/${encodeURIComponent(id)}`, data),

  review: (id: string, data: ReviewReceiptInput) =>
    api.put<Receipt>(`/receipts/${encodeURIComponent(id)}/review`, data),

  delete: (id: string, version: number) =>
    api.delete<{ deleted: true }>(
      `/receipts/${encodeURIComponent(id)}?version=${encodeURIComponent(String(version))}`,
    ),
};

export const commentApi = {
  list: (targetType: CommentTargetType, targetId: string, viewerLanguage?: Language) => {
    const sp = new URLSearchParams({
      targetType,
      targetId,
    });
    if (viewerLanguage) {
      sp.set('viewerLanguage', viewerLanguage);
    }
    return api.get<CommentListResponse>(`/comments?${sp.toString()}`);
  },

  create: (body: CreateCommentInput) => api.post<CommentWithUser>('/comments', body),
};

export const familyMemberApi = {
  list: (viewerLanguage?: Language) => {
    const sp = new URLSearchParams();
    if (viewerLanguage) {
      sp.set('viewerLanguage', viewerLanguage);
    }
    const query = sp.toString();
    const suffix = query ? `?${query}` : '';
    return withInFlightDedupe(
      () => `family-members:${viewerLanguage ?? ''}`,
      () => api.get<FamilyMemberListItem[]>(`/family-members${suffix}`),
    )();
  },

  get: (id: string) =>
    api.get<FamilyMember>(`/family-members/${encodeURIComponent(id)}`),

  create: (body: CreateFamilyMemberInput) =>
    api.post<FamilyMember>('/family-members', body),

  update: (id: string, body: CreateFamilyMemberInput) =>
    api.put<FamilyMember>(`/family-members/${encodeURIComponent(id)}`, body),

  delete: (id: string) =>
    api.delete<{ deleted: boolean }>(`/family-members/${encodeURIComponent(id)}`),
};

export const settingsApi = {
  get: () => api.get<UserSettings>('/settings'),

  update: (body: UpdateSettingsInput) => api.put<UserSettings>('/settings', body),

  getGettingStarted: () =>
    api.get<import('@fdh/shared').EmployerGettingStartedProgress>('/settings/getting-started'),

  dismissGettingStarted: () =>
    api.post<import('@fdh/shared').EmployerGettingStartedProgress>(
      '/settings/getting-started/dismiss',
      {},
    ),

  getAiUsage: () =>
    api.get<{
      ai?: { dailyLimit: number; usedToday: number; resetAt: string };
      receipt?: { dailyLimit: number; usedToday: number; resetAt: string };
      taskSubmit?: { dailyLimit: number; usedToday: number; resetAt: string };
    }>('/settings/ai-usage'),

  getUsage: () => api.get<UserUsageSummary>('/settings/usage'),

  requestAccountRemoval: (body: { password?: string }) =>
    api.post<{ requestedAt: string }>('/settings/account-removal-request', body),
  confirmAccountRemoval: (body: { token: string }) =>
    api.post<{ confirmedAt: string }>('/settings/account-removal-confirm', body),

  validateInvitationCode: (code: string) =>
    api.post<import('@fdh/shared').InvitationCodeEmployerValidateResult>(
      '/settings/invitation-code/validate',
      { code },
    ),

  redeemInvitationCode: (code: string) =>
    api.post<import('@fdh/shared').InvitationCodeRedeemResult>(
      '/settings/invitation-code/redeem',
      { code },
    ),

  getInvitationCodeReferrerStats: () =>
    api.get<import('@fdh/shared').InvitationCodeReferrerStats | null>(
      '/settings/invitation-code/referrer-stats',
    ),
};

export const publicMembershipPricingApi = {
  get: (locale: MembershipPricingLocale) =>
    api.get<PublicMembershipPricingResponse>(
      `/public/membership-pricing?locale=${encodeURIComponent(locale)}`,
    ),
};

export type PublicConfigResponse = {
  frontendUrl: string;
  playStoreShowOfficialWebsiteUrl: boolean;
};

export const publicConfigApi = {
  get: () => api.get<PublicConfigResponse>('/public/config'),
};

export const pairingImportApi = {
  getModal: () =>
    api.get<PairingImportModalState>('/pairing/import-from-previous/modal'),
  getPreview: () =>
    api.get<PairingImportPreview>('/pairing/import-from-previous'),
  import: (body: PairingImportBodySchema) =>
    api.post<PairingImportResult>('/pairing/import-from-previous', body),
  dismissModal: () =>
    api.post<{ dismissed: boolean }>('/pairing/import-from-previous/dismiss-modal', {}),
};

export const membershipApi = {
  getRestoreModal: () =>
    api.get<RestoreArchivedTasksModalState>('/membership/restore-archived-tasks/modal'),
  listRestorableArchivedTasks: () =>
    api.get<{ items: RestorableArchivedTaskItem[] }>('/membership/restore-archived-tasks'),
  restoreArchivedTasks: (taskIds: string[]) =>
    api.post<RestoreArchivedTasksResult>('/membership/restore-archived-tasks', { taskIds }),
  dismissRestoreModal: () =>
    api.post<{ dismissed: boolean }>('/membership/restore-archived-tasks/dismiss-modal', {}),
};

export const subscriptionApi = {
  getStatus: () =>
    api.get<SubscriptionStatusSummary & { stripeConfigured: boolean }>(
      `/subscription/status?t=${Date.now()}`,
    ),
  getPreview: () => api.get<SubscriptionPreview>(`/subscription/preview?t=${Date.now()}`),
  listInvoices: () => api.get<SubscriptionInvoiceListResult>(`/subscription/invoices?t=${Date.now()}`),
  createCheckout: (billingInterval: 'month' | 'year' = 'month') =>
    api.post<SubscriptionCheckoutResult>('/subscription/checkout', { billingInterval }),
  createPortalSession: () => api.post<SubscriptionPortalResult>('/subscription/portal', {}),
  scheduleIntervalChange: (billingInterval: 'month' | 'year') =>
    api.post<{
      billingInterval: 'month' | 'year';
      pendingBillingInterval: 'month' | 'year';
      intervalChangeAt: string;
      lockedPromo: SubscriptionStatusSummary['lockedPromo'];
    }>('/subscription/interval-change', { billingInterval }),
  cancelIntervalChange: () =>
    api.delete<SubscriptionStatusSummary>('/subscription/interval-change'),
};

export const healthApi = {
  get: () =>
    api.get<{
      status: string;
      features: { translationEnabled: boolean };
      maintenance?: {
        active: boolean;
        message: { 'zh-Hant': string; 'zh-Hans': string; en: string };
        publicApiEnabled: boolean;
        adminPortalEnabled: boolean;
      };
      breakGlassPath?: string;
    }>('/health'),
};

export const authApi = {
  googleAuth: (body: {
    idToken: string;
    mode: 'login' | 'register';
    nickname?: string;
    role?: 'employer' | 'helper';
    locale?: Language;
    usageRegion?: UsageRegion;
    invitationCode?: string;
  }) => api.post<{ accessToken: string; user: UserProfile }>('/auth/google', body),

  changePassword: (body: {
    currentPassword: string;
    newPassword: string;
    confirmNewPassword: string;
  }) => api.post<{ message?: string }>('/auth/change-password', body),

  resendVerification: () => api.post<{ message?: string }>('/auth/resend-verification'),
};

export const notificationApi = {
  list: (page = 1, pageSize = 20) =>
    api.get<NotificationListResponse>(
      `/notifications?page=${page}&pageSize=${pageSize}`,
    ),

  unreadCount: () =>
    api.get<NotificationUnreadCountResponse>('/notifications/unread-count'),

  markRead: (id: string) =>
    api.put<{ id: string; read: boolean }>(`/notifications/${id}/read`),

  markAllRead: () => api.put<{ updatedCount: number }>('/notifications/read-all'),

  getPushPublicKey: () =>
    api.get<{ enabled: boolean; publicKey: string }>('/notifications/push/public-key'),

  subscribePush: (subscription: PushSubscriptionJSON) =>
    api.post<{ subscribed: boolean }>('/notifications/push/subscribe', {
      subscription,
    }),

  unsubscribePush: (endpoint: string) =>
    api.post<{ removed: boolean }>('/notifications/push/unsubscribe', { endpoint }),
};

export const faqApi = {
  list: (params: { role?: FaqAudienceRole; language?: Language } = {}) => {
    const sp = new URLSearchParams();
    if (params.role) {
      sp.set('role', params.role);
    }
    if (params.language) {
      sp.set('language', params.language);
    }
    const query = sp.toString();
    return api.get<{ faqs: FaqLocalizedItem[] }>(`/faqs${query ? `?${query}` : ''}`);
  },
};

export const legalDocumentApi = {
  getActive: (documentType: LegalDocumentType, locale: LegalDocumentLocale = 'en') =>
    api.get<LegalDocumentPublic>(
      `/legal-documents/${encodeURIComponent(documentType)}?locale=${encodeURIComponent(locale)}`,
    ),
};

export const pinMessageApi = {
  list: (includeInactive = false, viewerLanguage?: Language) => {
    const sp = new URLSearchParams({
      includeInactive: includeInactive ? 'true' : 'false',
    });
    if (viewerLanguage) {
      sp.set('viewerLanguage', viewerLanguage);
    }
    return api.get<PinMessageListResponse>(`/pin-messages?${sp.toString()}`);
  },

  getLimits: () => api.get<PinMessageLimits>('/pin-messages/limits'),

  create: (body: CreatePinMessageInput) => api.post<PinMessage>('/pin-messages', body),

  update: (id: string, body: UpdatePinMessageInput) =>
    api.put<PinMessage>(`/pin-messages/${encodeURIComponent(id)}`, body),

  remove: (id: string) => api.delete<{ deleted: boolean }>(`/pin-messages/${encodeURIComponent(id)}`),

  reorder: (body: ReorderPinMessageInput) => api.put<{ updated: boolean }>('/pin-messages/reorder', body),
};

export const systemMessageApi = {
  list: (viewerLanguage?: Language, tickerOnly = false) => {
    const sp = new URLSearchParams();
    if (viewerLanguage) {
      sp.set('viewerLanguage', viewerLanguage);
    }
    if (tickerOnly) {
      sp.set('tickerOnly', 'true');
    }
    const qs = sp.toString();
    return api.get<SystemMessageListResponse>(`/system-messages${qs ? `?${qs}` : ''}`);
  },

  markRead: (id: string) =>
    api.put<{ read: boolean }>(`/system-messages/${encodeURIComponent(id)}/read`),
};

export const weatherGreetingApi = {
  current: () => api.get<WeatherGreetingCurrentResponse>('/weather-greetings/current'),
};

type InventoryAnalyzeResponse = {
  status: string;
  confidence: number;
  rawText: string;
};

export const inventoryApi = {
  analyze: (body: { itemName: string; mimeType: string; imageBase64: string }) =>
    api.post<InventoryAnalyzeResponse>('/inventory/analyze', body),

  submit: (instanceId: string, body: InventorySubmitInputSchema) =>
    api.post<{ success: boolean }>(
      `/inventory/task-instances/${encodeURIComponent(instanceId)}/submit`,
      body,
    ),

  resubmit: (instanceId: string, body: InventorySubmitInputSchema) =>
    api.put<{ success: boolean }>(
      `/inventory/task-instances/${encodeURIComponent(instanceId)}/resubmit`,
      body,
    ),

  getTaskMetadataSuggestions: () =>
    api.get<{
      categories: string[];
      locations: string[];
      maxInventoryLocations: number | null;
      maxInventoryCategories: number | null;
    }>('/inventory/task-metadata/suggestions'),

  getTaskInstanceWizard: (instanceId: string) =>
    api.get<{
      taskId: string;
      title: string;
      description: string;
      categories: string[];
      locations: string[];
      notifyExpiryLeadDays: number[];
      latestSubmissionCycleNo: number;
      existingSubmission: unknown;
    }>(`/inventory/task-instances/${encodeURIComponent(instanceId)}/wizard`),

  getSubmissionReviewSummary: (instanceId: string) =>
    api.get<{
      locations: Array<{
        locationName: string;
        photoCount: number;
        hasAnyItem: boolean;
        itemCount: number;
        categoriesWithItems: string[];
        categoriesNoItems: string[];
      }>;
    }>(`/inventory/submissions/${encodeURIComponent(instanceId)}/review`),

  getSettings: () => api.get<{ helperCanEditItems: boolean }>('/inventory/settings'),

  listItems: () =>
    api.get<
      { id: string; name: string; category: string; createdByRole: string; createdAt: string }[]
    >('/inventory/items'),

  createItem: (body: { name: string; category?: string }) =>
    api.post<{ id: string }>('/inventory/items', body),

  updateItem: (itemId: string, body: { name?: string; category?: string }) =>
    api.put<{ success: boolean }>(`/inventory/items/${encodeURIComponent(itemId)}`, body),

  listLists: () =>
    api.get<
      {
        id: string;
        name: string;
        scheduleType: string;
        scheduleWeekday: number | null;
        itemCount: number;
      }[]
    >('/inventory/lists'),

  getListLines: (listId: string) =>
    api.get<{
      items: { itemId: string; itemName: string; sortOrder: number }[];
      hiddenItemIds: string[];
    }>(`/inventory/lists/${encodeURIComponent(listId)}/lines`),

  createList: (body: { name: string }) => api.post<{ id: string }>('/inventory/lists', body),
  updateList: (listId: string, body: { name: string }) =>
    api.put<{ success: boolean }>(`/inventory/lists/${encodeURIComponent(listId)}`, body),

  listActiveTasksForList: (listId: string) =>
    api.get<{ taskId: string; title: string }[]>(
      `/inventory/lists/${encodeURIComponent(listId)}/active-tasks`,
    ),

  removeList: (listId: string) =>
    api.delete<{ success: boolean }>(`/inventory/lists/${encodeURIComponent(listId)}`),

  putListItems: (listId: string, itemIds: string[]) =>
    api.put<{ success: boolean }>(`/inventory/lists/${encodeURIComponent(listId)}/items`, { itemIds }),

  hideListItem: (listId: string, itemId: string) =>
    api.post<{ success: boolean }>(
      `/inventory/lists/${encodeURIComponent(listId)}/items/${encodeURIComponent(itemId)}/hide`,
    ),

  unhideListItem: (listId: string, itemId: string) =>
    api.post<{ success: boolean }>(
      `/inventory/lists/${encodeURIComponent(listId)}/items/${encodeURIComponent(itemId)}/unhide`,
    ),

  createTask: (body: {
    inventoryListId?: string;
    title?: string;
    description: string;
    recurrence: string;
    recurrenceDays: number[];
    monthlyKind?: string | null;
    recurrenceMonthDays?: number[];
    recurrenceOrdinal?: number | null;
    occurrenceDate: string;
    startDate: string;
    endDate: string;
    exclusionRanges: { startDate: string; endDate: string }[];
    dueTime: string;
    requirePhoto: boolean;
    notifyEmployer: boolean;
    remindHelperBeforeDue?: boolean;
    categories?: string[];
    locations?: string[];
    notifyExpiryLeadDays?: number[];
  }) => api.post<{ taskId: string; instanceId: string | null }>('/inventory/tasks', body),
};

export { getStoredToken, setStoredToken, removeStoredToken, tryRefreshToken };

// Re-export types for convenience
export type { ApprovalStatus };
